import { useIntegrationCatalog } from "#app/components/integrations/use-integration-catalog";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";
import { usePermission } from "#app/lib/hooks/use-permission";

/**
 * Which of the three route-less "Manage" dialogs the viewer may open: the
 * Custom Field Manager, the Automations manager and the App Center. Every
 * door into them (workspace menu, settings sidebar, global search commands)
 * reads these flags so a door never shows where the dialog would refuse.
 *
 * - Custom fields: the `custom_fields.manage` permission.
 * - Automations: the feature flag AND `canOpenManager` (a composite of
 *   permissions, not one flat key).
 * - App Center: `workspace.integrations` AND a non-empty provider catalog —
 *   every provider can be flag-gated off, and an empty hub is worse than no
 *   door. The catalog is fetched only when the permission passes, and the hub
 *   dialog shares the query's cache. `providers` is empty during the initial
 *   fetch, so the door stays hidden until it resolves (no flash).
 */
export function useManageDialogDoors(): {
  showCustomFields: boolean;
  showAutomations: boolean;
  showIntegrations: boolean;
} {
  const showCustomFields = usePermission("custom_fields.manage");
  const automationsEnabled = useAutomationsEnabled();
  const { canOpenManager: canManageAutomations } = useAutomationPermissions();
  const canManageIntegrations = usePermission("workspace.integrations");
  const { providers: integrationProviders } = useIntegrationCatalog({
    skip: !canManageIntegrations,
  });
  return {
    showCustomFields,
    showAutomations: automationsEnabled && canManageAutomations,
    showIntegrations: canManageIntegrations && integrationProviders.length > 0,
  };
}
